import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import {
  Perm,
  type ProductListItem,
  type Product,
  type Purchase,
  type PurchaseDetail,
  type Supplier,
  type Variant,
} from '../api/types'
import { formatMoney, formatMoment, formatQty, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Row, Select, Spinner, TextInput,
} from '@shop/ui'
import { purchaseStatusNames, receiptStateNames, receiptStateTone } from '../ui/purchasing'

/**
 * قائمةُ أوامر الشراء.
 *
 * <b>وتُعرض الحالتان معًا</b>: «معتمَد · مستلَمٌ جزئيًّا (٦٠ من ١٠٠)». الأولى ما
 * قرّره إنسان، والثانيةُ ما يقوله الحساب — كلٌّ من مصدره، ولا انحرافَ ممكنٌ
 * بينهما لأنّ الثانيةَ ليست عمودًا في أيّ جدول.
 */
export default function Purchases() {
  const { can } = useAuth()
  const [status, setStatus] = useState('')

  const purchases = useQuery({
    queryKey: ['purchases', status],
    queryFn: () => api<Purchase[]>(`/api/v1/purchases?status=${status}`),
  })

  return (
    <Panel
      title="أوامرُ الشراء"
      actions={can(Perm.purchaseCreate) && <NewPurchaseButton />}
    >
      <div className="mb-4">
        <Field label="الحالة">
          <Select value={status} onChange={setStatus}>
            <option value="">الكلّ</option>
            <option value="DRAFT">مسوّدة</option>
            <option value="CONFIRMED">معتمَد</option>
            <option value="CLOSED">مُغلَق</option>
            <option value="CANCELLED">ملغًى</option>
          </Select>
        </Field>
      </div>

      {purchases.isPending && <Spinner />}
      {purchases.isError && <ErrorBanner error={purchases.error} />}
      {purchases.data?.length === 0 && <Empty>لا أوامرَ مطابقة.</Empty>}

      <ul className="space-y-2">
        {(purchases.data ?? []).map((purchase) => (
          <li key={purchase.id}>
            <Link
              to={`/purchases/${purchase.id}`}
              className="block rounded-md border border-line px-3 py-2 hover:bg-surface"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{purchase.supplierName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">
                  {purchase.purchaseNo}
                </span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span>{purchaseStatusNames[purchase.status] ?? purchase.status}</span>

                {/* الحالةُ المشتقّة — ومعها الرقمان، فالنسبةُ وحدَها لا تُفيد */}
                {purchase.status === 'CONFIRMED' && (
                  <span className={receiptStateTone(purchase.receiptState) === 'warn' ? 'text-warn' : ''}>
                    {receiptStateNames[purchase.receiptState]}
                    {purchase.receiptState !== 'NOT_RECEIVED' && (
                      <span className="tabular">
                        {' '}
                        ({formatQty(purchase.receivedQty)} من {formatQty(purchase.orderedQty)})
                      </span>
                    )}
                  </span>
                )}

                <span className="tabular">{purchase.documentDate}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/** إنشاءُ أمرٍ: مورّدٌ وفرعٌ ثمّ سطور. والسطورُ في شاشة الأمر لا هنا. */
function NewPurchaseButton() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [supplierId, setSupplierId] = useState('')
  const [branchId, setBranchId] = useState('')

  const suppliers = useQuery({
    queryKey: ['suppliers', '', false],
    queryFn: () => api<Supplier[]>('/api/v1/suppliers'),
    enabled: open,
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api<{ id: string; nameAr: string }[]>('/api/v1/branches'),
    enabled: open,
  })

  // فرعٌ واحدٌ يُختار من نفسه، وأكثرُ يُسأل عنه. واختيارُ الأوّل صامتًا كان
  // سيُنشئ أوامرَ في فرعٍ لم يقصده أحدٌ يوم يصير للمحلّ فرعان.
  const only = branches.data?.length === 1 ? branches.data[0].id : ''
  const chosenBranch = branchId || only

  const create = useMutation({
    mutationFn: () =>
      api<Purchase>('/api/v1/purchases', {
        method: 'POST',
        body: { supplierId, branchId: chosenBranch },
      }),
    onSuccess: async (purchase) => {
      await queryClient.invalidateQueries({ queryKey: ['purchases'] })
      setOpen(false)

      // ‏`navigate` لا `window.location`: الثانيةُ تُعيد تحميلَ التطبيق كلِّه،
      // فتُفقد الجلسةَ من الذاكرة ويومض بابُ الدخول قبل أن يعود التدوير.
      void navigate(`/purchases/${purchase.id}`)
    },
  })

  if (!open) {
    return <Button tone="primary" onClick={() => setOpen(true)}>أمرٌ جديد</Button>
  }

  return (
    <div className="w-full space-y-2 rounded-md border border-line bg-surface p-3">
      <Field label="المورّد">
        <Select value={supplierId} onChange={setSupplierId}>
          <option value="">اختر مورّدًا…</option>
          {(suppliers.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.nameAr}</option>
          ))}
        </Select>
      </Field>

      {(branches.data?.length ?? 0) > 1 && (
        <Field label="الفرع">
          <Select value={chosenBranch} onChange={setBranchId}>
            <option value="">اختر فرعًا…</option>
            {(branches.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>{b.nameAr}</option>
            ))}
          </Select>
        </Field>
      )}

      {create.isError && <ErrorBanner error={create.error} />}

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={!supplierId || !chosenBranch || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'يُنشأ…' : 'إنشاء'}
        </Button>

        <Button onClick={() => setOpen(false)}>إلغاء</Button>
      </div>
    </div>
  )
}

/**
 * أمرُ شراءٍ بتفاصيله وسطوره.
 *
 * <b>والسطورُ تُعدَّل في المسوّدة وحدَها</b>: تعديلُ «المطلوب» بعد استلام جزءٍ
 * منه يُعيد كتابةَ ما وافق عليه المورّد، ويجعل نسبةَ التنفيذ رقمًا يتحرّك تحت
 * القارئ. ويحرس ذلك الخادمُ ومُشغّلٌ في القاعدة — وهذه الشاشةُ تُخفي الزرَّ فقط.
 */
export function PurchaseDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()

  const purchase = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => api<PurchaseDetail>(`/api/v1/purchases/${id}`),
    enabled: Boolean(id),
  })

  if (purchase.isPending) {
    return <Spinner />
  }

  if (purchase.isError) {
    return <ErrorBanner error={purchase.error} />
  }

  const { header, lines } = purchase.data!
  const isDraft = header.status === 'DRAFT'

  return (
    <div className="space-y-4">
      <Panel title={`أمرُ شراء ${header.purchaseNo}`}>
        <dl className="space-y-1 text-sm">
          <Row label="المورّد">{header.supplierName}</Row>
          <Row label="الحالة">{purchaseStatusNames[header.status] ?? header.status}</Row>

          {header.status === 'CONFIRMED' && (
            <Row label="الاستلام">
              {receiptStateNames[header.receiptState]}
              <span className="tabular">
                {' '}
                — {formatQty(header.receivedQty)} من {formatQty(header.orderedQty)}،
                والمتبقّي {formatQty(header.remainingQty)}
              </span>
            </Row>
          )}

          <Row label="التاريخ">{header.documentDate}</Row>
          {header.supplierRef && <Row label="مرجعُ المورّد">{header.supplierRef}</Row>}
          {can(Perm.costView) && <Row label="الإجمالي">{formatMoney(header.netTotal, header.currency)}</Row>}
          <Row label="أُنشئ">{formatMoment(header.createdAt)}</Row>
        </dl>

        <div className="mt-4 flex flex-wrap gap-2">
          {isDraft && can(Perm.purchaseConfirm) && <ConfirmControl purchaseId={header.id} />}
          {can(Perm.purchaseCancel) && <FinishControl purchase={header} />}
        </div>
      </Panel>

      <Panel title="السطور">
        {lines.length === 0 && <Empty>لا سطورَ بعد.</Empty>}

        <ul className="space-y-2">
          {lines.map((line) => (
            <li key={line.id} className="rounded-md border border-line px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{line.productName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{line.sku}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span className="tabular">طُلب {formatQty(line.orderedQty)}</span>

                {header.status === 'CONFIRMED' && (
                  <>
                    <span className="tabular">استُلم {formatQty(line.receivedQty)}</span>
                    <span className={`tabular ${line.remainingQty > 0 ? 'text-warn' : ''}`}>
                      المتبقّي {formatQty(line.remainingQty)}
                    </span>
                  </>
                )}

                {can(Perm.costView) && (
                  <span className="tabular">{formatMoney(line.netAmount, header.currency)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {isDraft && can(Perm.purchaseEdit) ? (
          <div className="mt-3">
            <LineEditor purchaseId={header.id} lines={lines} />
          </div>
        ) : (
          !isDraft && (
            <div className="mt-3">
              <Banner tone="info">
                سطورُ أمرٍ اعتُمد لا تُعدَّل — فنسبةُ التنفيذ تبقى صادقة. وما لن يصل
                يُغلق الأمرُ بسببه.
              </Banner>
            </div>
          )
        )}
      </Panel>
    </div>
  )
}

/**
 * تحريرُ سطور المسوّدة — <b>وتُرسَل كلُّها دفعةً واحدة</b>.
 *
 * ولا نقطةَ «أضِف سطرًا» ولا «احذف سطرًا»: السطورُ تُستبدل كاملةً، فلا يوجد
 * ترتيبٌ من عمليّاتٍ جزئيّةٍ يترك الأمرَ نصفَ محرَّرٍ إن سقطت إحداها.
 */
function LineEditor({ purchaseId, lines }: { purchaseId: string; lines: PurchaseLineRow[] }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<DraftLine[]>(() =>
    lines.map((l) => ({
      variantId: l.variantId,
      label: `${l.productName} · ${l.sku}`,
      qty: l.orderedQty.toString(),
      cost: l.unitCost.toString(),
    })),
  )

  const save = useMutation({
    mutationFn: () =>
      api<PurchaseDetail>(`/api/v1/purchases/${purchaseId}/lines`, {
        method: 'PUT',
        body: {
          lines: draft.map((d) => ({
            variantId: d.variantId,
            orderedQty: toNumber(d.qty) ?? 0,
            unitCost: toNumber(d.cost) ?? 0,
          })),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['purchase', purchaseId] })
    },
  })

  const incomplete = draft.some((d) => (toNumber(d.qty) ?? 0) <= 0)

  return (
    <div className="space-y-3">
      {draft.map((line, index) => (
        <div key={`${line.variantId}-${index}`} className="rounded-md border border-line p-3">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium">{line.label}</span>
            <Button
              onClick={() => setDraft(draft.filter((_, i) => i !== index))}
              title="احذف هذا السطر"
            >
              حذف
            </Button>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="الكميّة">
              <NumberInput
                value={line.qty}
                onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, qty: v } : d)))}
              />
            </Field>

            <Field label="كلفةُ الوحدة">
              <NumberInput
                value={line.cost}
                onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, cost: v } : d)))}
              />
            </Field>
          </div>
        </div>
      ))}

      <VariantPicker
        onPick={(variantId, label) =>
          setDraft([...draft, { variantId, label, qty: '1', cost: '0' }])
        }
      />

      {save.isError && <ErrorBanner error={save.error} />}

      <Button
        tone="primary"
        disabled={draft.length === 0 || incomplete || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'تُحفظ…' : 'حفظُ السطور'}
      </Button>
    </div>
  )
}

type DraftLine = { variantId: string; label: string; qty: string; cost: string }

type PurchaseLineRow = PurchaseDetail['lines'][number]

/**
 * اختيارُ صنفٍ ثمّ متغيّرِه — <b>خطوتان لا خطوة</b>.
 *
 * فالمخزونُ على المتغيّر لا على المنتج (ق-١): «آيفون ١٥» ليس صنفًا يُشترى —
 * يُشترى «آيفون ١٥ أسود ١٢٨». واختيارُ المتغيّر الافتراضيّ صامتًا كان سيُدخل
 * الكميّةَ على لونٍ لم يقصده أحد، ولا يُكتشف إلا عند الجرد.
 *
 * <b>والبحثُ في الخادم</b>: تطبيعُ العربيّة يقع هناك بالجداول نفسِها التي
 * تُطابق بها المسوّدات، وترشيحٌ في المتصفّح كان سيقول «لا نتائج» لاسمٍ موجودٍ
 * بألفٍ مختلفة.
 */
function VariantPicker({ onPick }: { onPick: (variantId: string, label: string) => void }) {
  const [term, setTerm] = useState('')
  const [productId, setProductId] = useState<string | null>(null)

  const results = useQuery({
    queryKey: ['products', term],
    queryFn: () => api<ProductListItem[]>(`/api/v1/products?limit=20&q=${encodeURIComponent(term)}`),
    enabled: term.trim().length > 1 && productId === null,
  })

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api<Product>(`/api/v1/products/${productId}`),
    enabled: productId !== null,
  })

  if (productId !== null) {
    return (
      <div className="rounded-md border border-dashed border-line p-3">
        {product.isPending && <Spinner />}
        {product.isError && <ErrorBanner error={product.error} />}

        {product.data && (
          <>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{product.data.nameAr}</span>
              <Button onClick={() => setProductId(null)}>تراجع</Button>
            </div>

            <ul className="space-y-1">
              {product.data.variants
                .filter((variant) => variant.isActive)
                .map((variant) => (
                  <li key={variant.id}>
                    <button
                      type="button"
                      className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
                      onClick={() => {
                        onPick(variant.id, `${product.data!.nameAr} · ${describeVariant(variant)}`)
                        setProductId(null)
                        setTerm('')
                      }}
                    >
                      {describeVariant(variant)}
                      <span className="tabular mx-2 text-xs text-ink-soft" dir="ltr">{variant.sku}</span>
                    </button>
                  </li>
                ))}
            </ul>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-md border border-dashed border-line p-3">
      <Field label="أضِف صنفًا">
        <TextInput value={term} onChange={setTerm} placeholder="ابحث بالاسم أو الطراز…" />
      </Field>

      {results.isPending && term.trim().length > 1 && <Spinner />}

      <ul className="mt-2 space-y-1">
        {(results.data ?? []).map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
              onClick={() => setProductId(row.id)}
            >
              {row.nameAr}
              <span className="tabular mx-2 text-xs text-ink-soft" dir="ltr">{row.code}</span>
              {row.variantCount > 1 && (
                <span className="tabular text-xs text-ink-soft">· {row.variantCount} متغيّر</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** وصفُ المتغيّر كما يقرؤه الموظّف — واسمُه إن سُمّي، وإلا لونُه وسعتُه. */
function describeVariant(variant: Variant): string {
  const parts = [variant.variantName, variant.colour, variant.capacity].filter(Boolean)

  return parts.length > 0 ? parts.join(' · ') : 'المتغيّرُ الافتراضيّ'
}

function ConfirmControl({ purchaseId }: { purchaseId: string }) {
  const queryClient = useQueryClient()

  const confirm = useMutation({
    mutationFn: () =>
      api<PurchaseDetail>(`/api/v1/purchases/${purchaseId}/confirm`, { method: 'POST', body: {} }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['purchase', purchaseId] })
      await queryClient.invalidateQueries({ queryKey: ['purchases'] })
    },
  })

  return (
    <>
      <Button tone="primary" disabled={confirm.isPending} onClick={() => confirm.mutate()}>
        {confirm.isPending ? 'يُعتمد…' : 'اعتمادُ الأمر'}
      </Button>

      {confirm.isError && <ErrorBanner error={confirm.error} />}
    </>
  )
}

/**
 * إنهاءُ الأمر: إلغاءٌ قبل أوّل استلام، وإغلاقٌ بعده.
 *
 * <b>والفرقُ ليس تسمية</b>: أمرٌ «ملغًى» وله بضاعةٌ دخلت المخزنَ كذبٌ صريح —
 * الرصيدُ يقول إنّها وصلت والمستندُ يقول إنّه لم يكن. والشاشةُ تختار الفعلَ
 * بنفسها من الرقم، فلا يُحمَّل الموظّفُ تمييزًا يخطئ فيه.
 */
function FinishControl({ purchase }: { purchase: Purchase }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const received = purchase.receivedQty > 0
  const action = received ? 'close' : 'cancel'

  const finish = useMutation({
    mutationFn: () =>
      api<PurchaseDetail>(`/api/v1/purchases/${purchase.id}/${action}`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['purchase', purchase.id] })
      await queryClient.invalidateQueries({ queryKey: ['purchases'] })
      setAsking(false)
    },
  })

  if (purchase.status !== 'DRAFT' && purchase.status !== 'CONFIRMED') {
    return null
  }

  if (!asking) {
    return <Button onClick={() => setAsking(true)}>{received ? 'إغلاقُ الأمر' : 'إلغاءُ الأمر'}</Button>
  }

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        {received
          ? 'الأمرُ استُلم منه شيء، فلا يُلغى — يُغلق بسببٍ مكتوب. ولو وصل الباقي لاحقًا يُعاد فتحُه.'
          : 'لا استلامَ بعد، فيُلغى. وبعد أوّل إيصالٍ لا يبقى إلا الإغلاق.'}
      </Banner>

      <Field label="السبب" hint="عشرةُ أحرفٍ فأكثر — يُسأل عنه بعد سنة">
        <TextInput value={reason} onChange={setReason} placeholder="المورّدُ اعتذر عن الباقي" />
      </Field>

      {finish.isError && <ErrorBanner error={finish.error} />}

      <div className="flex gap-2">
        <Button
          tone="danger"
          disabled={reason.trim().length < 10 || finish.isPending}
          onClick={() => finish.mutate()}
        >
          {finish.isPending ? 'يُنفَّذ…' : received ? 'تأكيدُ الإغلاق' : 'تأكيدُ الإلغاء'}
        </Button>

        <Button onClick={() => setAsking(false)}>تراجع</Button>
      </div>
    </div>
  )
}
